/**
 * 基础组件样式定义
 * 定义Material-UI基础组件的样式变体
 */

import { SxProps, Theme } from "@mui/material/styles";
import {
  colors,
  spacing,
  borderRadius,
  shadows,
  typography,
} from "./variables";
import { composeStyles, themeUtils } from "./utils";

// 样式变体工具函数
const variantStyles = {
  // 按钮变体
  button: (
    variant: "contained" | "outlined" | "text",
    color: keyof typeof colors = "primary"
  ) => {
    const baseStyles: SxProps<Theme> = {
      borderRadius: "8px",
      textTransform: "none",
      fontWeight: typography.fontWeight.medium,
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: shadows.md,
      },
    };

    switch (variant) {
      case "contained":
        return composeStyles(baseStyles, {
          backgroundColor: themeUtils.color(`${color}.main`),
          color: themeUtils.color(`${color}.contrastText`),
          "&:hover": {
            backgroundColor: themeUtils.color(`${color}.dark`),
          },
        });

      case "outlined":
        return composeStyles(baseStyles, {
          border: `2px solid`,
          borderColor: themeUtils.color(`${color}.main`),
          color: themeUtils.color(`${color}.main`),
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: themeUtils.color(`${color}.main`),
            color: themeUtils.color(`${color}.contrastText`),
          },
        });

      case "text":
        return composeStyles(baseStyles, {
          color: themeUtils.color(`${color}.main`),
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: themeUtils.color(`${color}.light`),
            color: themeUtils.color(`${color}.dark`),
          },
        });

      default:
        return baseStyles;
    }
  },

  // 输入框变体
  textField: (variant: "standard" | "outlined" | "filled" = "outlined") => {
    const baseStyles: SxProps<Theme> = {
      "& .MuiOutlinedInput-root": {
        borderRadius: borderRadius.none,
        "& fieldset": {
          borderColor: colors.divider,
          borderWidth: "2px",
        },
        "&:hover fieldset": {
          borderColor: themeUtils.color("primary.main"),
        },
        "&.Mui-focused fieldset": {
          borderColor: themeUtils.color("primary.main"),
          borderWidth: "2px",
        },
      },
      "& .MuiInputLabel-root": {
        color: colors.text.secondary,
        "&.Mui-focused": {
          color: themeUtils.color("primary.main"),
        },
      },
      "& .MuiInputBase-input": {
        color: colors.text.primary,
      },
    };

    return baseStyles;
  },

  // 卡片变体
  card: (elevation: keyof typeof shadows = "md") => ({
    borderRadius: borderRadius.none,
    boxShadow: themeUtils.shadow(elevation),
    "&:hover": {
      boxShadow: themeUtils.shadow("lg"),
    },
  }),
};

// AppBar 组件样式
export const appBarStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    backgroundColor: "background.paper",
    color: "text.primary",
    boxShadow: "none",
    borderBottom: "1px solid",
    borderColor: "divider",
  },

  // 头部样式
  header: {
    background: (theme) =>
      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    backdropFilter: "blur(10px)",
    backgroundColor: "rgba(25, 118, 210, 0.95)",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    zIndex: (theme) => theme.zIndex.drawer + 1,
    transition: "all 0.3s ease-in-out",
    width: "100%",
  },

  // 主内容区域样式
  main: {
    backgroundColor: "background.default",
    color: "text.primary",
    zIndex: (theme) => theme.zIndex.drawer + 1,
    transition: (theme) =>
      theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
  },
};

// Toolbar 组件样式
export const toolbarStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    minHeight: "64px !important",
    px: 3,
    gap: 2,
    borderRadius: "0px",
  },

  // 头部工具栏样式
  header: {
    minHeight: { xs: 60, sm: 68 },
    px: { xs: 2, sm: 3 },
    gap: { xs: 1, sm: 2 },
    borderRadius: "0px",
  },
};

// IconButton 组件样式
export const iconButtonStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    borderRadius: "0px",
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },

  // 头部图标按钮样式
  header: {
    borderRadius: "0px",
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },

  // 菜单按钮样式
  menuButton: {
    color: "white",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.15)",
      transform: "scale(1.1)",
    },
    transition: "all 0.2s ease-in-out",
    borderRadius: "8px",
    p: 1,
  },

  // 主题切换按钮样式
  themeToggle: {
    color: "white",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.15)",
      transform: "rotate(180deg)",
    },
    transition: "all 0.3s ease-in-out",
    borderRadius: "8px",
  },

  // 通知按钮样式
  notification: {
    color: "white",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.15)",
      transform: "scale(1.1)",
    },
    transition: "all 0.2s ease-in-out",
    borderRadius: "8px",
  },
};

// Avatar 组件样式
export const avatarStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    borderRadius: "0px",
    cursor: "pointer",
    "&:hover": {
      opacity: 0.8,
    },
  },

  // 头部头像样式
  header: {
    bgcolor: "#FFFFFF",
    width: { xs: 34, sm: 38 },
    height: { xs: 34, sm: 38 },
    fontSize: { xs: "1rem", sm: "1.1rem" },
    fontWeight: 700,
    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
    borderRadius: "8px",
    cursor: "pointer",
    border: "1px solid rgba(255,255,255,0.3)",
    "&:hover": {
      borderColor: "rgba(255,255,255,0.6)",
      backgroundColor: "rgba(255,255,255,0.9)",
      transform: "scale(1.05)",
    },
    transition: "all 0.2s ease-in-out",
  },

  // 主内容区域头像样式
  main: {
    bgcolor: "#fff",
    cursor: "pointer",
    borderRadius: "0px",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.04)",
    },
  },
};

// TextField 组件样式
export const textFieldStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: variantStyles.textField(),

  // 变体样式
  standard: variantStyles.textField("standard"),
  outlined: variantStyles.textField("outlined"),
  filled: variantStyles.textField("filled"),

  // 特殊类型
  dialog: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "0px",
    },
  },
  error: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "error.main",
      },
      "&:hover fieldset": {
        borderColor: "error.main",
      },
      "&.Mui-focused fieldset": {
        borderColor: "error.main",
      },
    },
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: "error.main",
      },
    },
  },
};

// Card 组件样式
export const cardStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: variantStyles.card(),

  // 变体样式
  sm: variantStyles.card("sm"),
  md: variantStyles.card("md"),
  lg: variantStyles.card("lg"),
  xl: variantStyles.card("xl"),

  // 特殊类型
  dashboard: {
    borderRadius: "0px",
    "&:hover": {
      boxShadow: 3,
    },
  },
  statCard: {
    borderRadius: "0px",
    "&:hover": {
      boxShadow: 3,
    },
  },
};

// CardContent 组件样式
export const cardContentStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    borderRadius: "0px",
    padding: 3,
  },

  // 特殊类型
  dashboard: {
    borderRadius: "0px",
    padding: 3,
  },
};

// Paper 组件样式
export const paperStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    borderRadius: "0px",
  },

  // 特殊类型
  uSecurity: {
    borderRadius: "0px",
  },
  dashboard: {
    borderRadius: "0px",
  },
};

// Typography 组件样式
export const typographyStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    color: "text.primary",
  },

  // 头部样式
  header: {
    flexGrow: 1,
    color: "text.primary",
  },

  // 标题样式
  headerTitle: {
    fontWeight: 700,
    fontSize: { xs: "1.1rem", sm: "1.4rem" },
    color: "white",
    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
    letterSpacing: "0.5px",
  },

  // 副标题样式
  headerSubtitle: {
    color: "rgba(255,255,255,0.8)",
    fontSize: "0.7rem",
    fontWeight: 500,
    display: { xs: "none", sm: "block" },
  },

  // 主内容区域标题样式
  mainTitle: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "row",
    justifyContent: "start",
    alignItems: "center",
    color: "primary.main",
  },

  // 弹性增长样式
  flexGrow: {
    flexGrow: 1,
  },
};

// Box 组件样式
export const boxStyles: Record<string, SxProps<Theme>> = {
  // 基础样式
  root: {
    display: "flex",
  },

  // 主内容区域样式
  main: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },

  // 主内容区域内容样式
  mainContent: {
    flexGrow: 1,
    p: 1,
  },

  // 用户信息容器样式
  userInfoContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "start",
    alignItems: "end",
    paddingLeft: (theme) => theme.spacing(1),
    paddingRight: (theme) => theme.spacing(1),
  },

  // 用户名样式
  username: {
    fontSize: 16,
  },

  // 头部Logo样式
  headerLogo: {
    display: "flex",
    alignItems: "center",
    flexGrow: 1,
    gap: 2,
  },

  // 头部操作区域样式
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: { xs: 1, sm: 2 },
  },

  // 横向弹性布局样式
  flexRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "end",
    alignItems: "center",
    flexGrow: 0,
  },
};

// 导出所有基础组件样式
export default {
  appBarStyles,
  toolbarStyles,
  iconButtonStyles,
  avatarStyles,
  textFieldStyles,
  cardStyles,
  cardContentStyles,
  paperStyles,
  typographyStyles,
  boxStyles,
};
